import React, { useState, useRef, useEffect } from "react"
import { useSearchParams } from "react-router-dom"
import { Card, CardBody, Spinner } from "reactstrap"
import { Search, Gamepad2, ArrowLeft, ExternalLink, ChevronDown } from "lucide-react"
import axios from "axios"
import SEO from "../../components/Common/SEO"
import BackButton from "../../components/Common/BackButton"
import { okara_api } from "../../utils/variables"
import { fetchRecentApps } from "../../utils/recentAppsApi"
import KeywordSuggestionsDetailsModal from "../../components/Common/Modal/KeywordSuggestionsDetailsModal"
import AppsTrackingPlanModal from "../../components/Common/Modal/AppsTrackingPlanModal"
import RecentApps from "../../components/Common/RecentApps"
import { PAGE_SEO } from "../../config/seoConfig"
import { generateCanonicalUrl } from "../../utils/seoUtils"
import { formatDownloads } from "../../utils/numberUtils"
import toastr from "toastr"
import "toastr/build/toastr.min.css"
import { useAnalytics, ANALYTICS_EVENTS } from "../../analytics"

// ─── Marketing Intelligence section ──────────────────────────────────────────
// ASO/keyword insights now live in the real Ads/SEO Intelligence sections below,
// so they're not repeated here. Everything else is pending a real data source.

const IntelCard = ({ title, icon, children, style = {} }) => (
    <div style={{
        background: "rgba(255,255,255,0.02)",
        border: "1px solid rgba(255,255,255,0.07)",
        borderRadius: "14px",
        padding: "20px",
        marginBottom: "16px",
        ...style,
    }}>
        <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "16px" }}>
            <span style={{ fontSize: "16px" }}>{icon}</span>
            <span style={{ color: "#fff", fontWeight: 600, fontSize: "15px" }}>{title}</span>
        </div>
        {children}
    </div>
)

const Badge = ({ label, color = "#F6255A" }) => (
    <span style={{
        background: `${color}18`, color, fontSize: "11px", fontWeight: 600,
        padding: "3px 9px", borderRadius: "6px", display: "inline-block",
    }}>{label}</span>
)

// A badge list that only shows the first `limit` items, with a "+N more" / "Show less"
// toggle when there are more - keeps long keyword/tag lists from overwhelming the card.
const ExpandableBadgeList = ({ items = [], color = "#F6255A", limit = 6, emptyText }) => {
    const [expanded, setExpanded] = useState(false)

    if (items.length === 0) {
        return emptyText ? <p style={{ color: "#6b7280", fontSize: "12px", margin: 0 }}>{emptyText}</p> : null
    }

    const visible = expanded ? items : items.slice(0, limit)

    return (
        <div style={{ display: "flex", flexWrap: "wrap", gap: "6px", alignItems: "center" }}>
            {visible.map((item, i) => <Badge key={i} label={item} color={color} />)}
            {items.length > limit && (
                <button
                    onClick={() => setExpanded(v => !v)}
                    style={{
                        background: "#F6255A", color: "#fff", fontSize: "11px", fontWeight: 700,
                        padding: "3px 10px", borderRadius: "6px", border: "none", cursor: "pointer",
                        display: "inline-block", lineHeight: "16px",
                    }}
                >
                    {expanded ? "Show less" : `+${items.length - limit} more`}
                </button>
            )}
        </div>
    )
}

// Friendly copy for each backend job_status stage, keyed lowercase. The backend's own
// `message` field is meant for logs/retries (e.g. "Retry in ~3 seconds") and isn't
// user-facing copy, so it's never shown directly - only used to pick one of these.
const JOB_STATUS_MESSAGES = {
    resolving_platform: "Looking up app platform...",
    finding_competitors: "Finding competitors...",
    fetching_ads: "Fetching ads for this app and its competitors...",
    pending: "Getting started...",
    queued: "Queued — starting shortly...",
    processing: "Crunching the numbers...",
    in_progress: "Still working on it...",
    loading: "Loading...",
}

const getFriendlyStatusMessage = (jobStatus, fallback) =>
    JOB_STATUS_MESSAGES[String(jobStatus || "").toLowerCase()] || fallback

// Accordion wrapper for the intelligence sections below the platform table - only one
// section is expanded at a time; clicking a header expands that section and collapses
// the rest.
//
// The header used to be plain transparent text with a small gray chevron - clear enough
// to anyone used to reading UI conventions, but client feedback was that non-technical
// users didn't register it as clickable at all. So this now looks and behaves like an
// actual button: a bordered card surface (same tokens `IntelCard` above uses, so it
// still reads as part of the same design system), a hover state that visibly reacts,
// an explicit "Show details"/"Hide details" label removing any ambiguity about what
// clicking it does, and the chevron sits inside a circular badge - a much more
// universally-recognized "expand/collapse" affordance than a bare arrow glyph.
const CollapsibleSection = ({ title, isOpen, onToggle, children }) => {
    const [isHovered, setIsHovered] = useState(false)

    return (
        <div style={{ marginTop: "24px" }}>
            <button
                onClick={onToggle}
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
                aria-expanded={isOpen}
                style={{
                    display: "flex", alignItems: "center", gap: "12px", width: "100%",
                    background: isHovered ? "rgba(255,255,255,0.045)" : "rgba(255,255,255,0.02)",
                    border: `1px solid ${isHovered ? "rgba(255,255,255,0.18)" : "rgba(255,255,255,0.08)"}`,
                    borderRadius: "14px",
                    padding: "16px 20px",
                    marginBottom: isOpen ? "16px" : 0,
                    cursor: "pointer", textAlign: "left",
                    transition: "background 0.15s ease, border-color 0.15s ease",
                }}
            >
                <div style={{ width: "3px", height: "20px", background: "#F6255A", borderRadius: "2px", flexShrink: 0 }} />
                <span style={{ color: "#fff", fontWeight: 700, fontSize: "18px", flex: 1 }}>{title}</span>
                <span style={{ color: "#9ca3af", fontSize: "13px", fontWeight: 500, whiteSpace: "nowrap" }}>
                    {isOpen ? "Hide details" : "Show details"}
                </span>
                <div style={{
                    width: "28px", height: "28px", borderRadius: "50%", flexShrink: 0,
                    background: isHovered ? "rgba(246,37,90,0.15)" : "rgba(255,255,255,0.06)",
                    border: `1px solid ${isHovered ? "rgba(246,37,90,0.4)" : "rgba(255,255,255,0.14)"}`,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    transition: "background 0.15s ease, border-color 0.15s ease",
                }}>
                    <ChevronDown
                        size={16}
                        color={isHovered ? "#F6255A" : "#9ca3af"}
                        style={{ transform: isOpen ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.2s ease" }}
                    />
                </div>
            </button>
            {isOpen && children}
        </div>
    )
}

const severityColor = (level) => {
    const l = String(level || "").toLowerCase()
    if (l === "high") return "#ef4444"
    if (l === "medium") return "#f59e0b"
    if (l === "low") return "#10b981"
    return "#9ca3af"
}

const positionColor = (position) => {
    const p = String(position || "").toLowerCase()
    if (p === "leader") return "#10b981"
    if (p === "challenger") return "#f59e0b"
    if (p === "niche" || p === "follower") return "#6b7280"
    return "#3b82f6"
}

const MarketingIntelligence = ({ data }) => {
    if (!data) return null

    if (data.job_status !== "completed") {
        return (
            <div className="text-center py-5" style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.07)", borderRadius: "14px" }}>
                <Spinner style={{ color: "#F6255A" }} />
                <p className="mt-3 text-white" style={{ fontFamily: "Rubik" }}>{getFriendlyStatusMessage(data.job_status, "Loading marketing intelligence...")}</p>
            </div>
        )
    }

    const {
        score,
        score_why,
        position,
        summary,
        key_findings = [],
        social = {},
        competitors = [],
        actions = [],
        content_plan = [],
        data: meta = {},
        generated_at,
        cached,
    } = data

    const scorePct = typeof score === "number" ? Math.max(0, Math.min(100, score)) : 0
    const socialPlatforms = social.platforms || []
    const accountFixes = social.account_fixes || []
    const captionKeywords = social.caption_and_ad_keywords || []
    const generatedAtLabel = generated_at && !isNaN(new Date(generated_at))
        ? new Date(generated_at).toLocaleString()
        : null

    return (
        <>
            {(generatedAtLabel || cached) && (
                <p style={{ color: "#6b7280", fontSize: "11px", margin: "0 0 12px 0", textAlign: "right" }}>
                    {generatedAtLabel && `Generated ${generatedAtLabel}`}
                    {generatedAtLabel && cached ? " · " : ""}
                    {cached && "Cached"}
                </p>
            )}

            {/* ── Score + Summary ──────────────────────────────── */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 2fr", gap: "12px", marginBottom: "12px" }}>
                <div style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.07)", borderRadius: "14px", padding: "20px", textAlign: "center" }}>
                    <p style={{ color: "#9ca3af", fontSize: "12px", marginBottom: "8px", textTransform: "uppercase", letterSpacing: "0.5px" }}>Marketing Score</p>
                    <div style={{ position: "relative", display: "inline-block" }}>
                        <svg width="80" height="80" viewBox="0 0 80 80">
                            <circle cx="40" cy="40" r="32" fill="none" stroke="rgba(255,255,255,0.05)" strokeWidth="8" />
                            <circle cx="40" cy="40" r="32" fill="none" stroke="#F6255A" strokeWidth="8"
                                strokeDasharray={`${(scorePct / 100) * 201} 201`}
                                strokeLinecap="round" transform="rotate(-90 40 40)" />
                        </svg>
                        <span style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", color: "#fff", fontWeight: 700, fontSize: "18px" }}>
                            {score != null ? Math.round(score) : "—"}
                        </span>
                    </div>
                    {position && (
                        <div style={{ marginTop: "10px" }}>
                            <Badge label={position} color={positionColor(position)} />
                        </div>
                    )}
                    {score_why && (
                        <p style={{ color: "#6b7280", fontSize: "11px", margin: "10px 0 0 0" }}>{score_why}</p>
                    )}
                </div>

                <IntelCard title="Summary" icon="🧭" style={{ marginBottom: 0 }}>
                    {summary && <p style={{ color: "#e5e7eb", fontSize: "13px", margin: "0 0 12px 0" }}>{summary}</p>}
                    {key_findings.map((finding, i) => (
                        <div key={i} style={{ display: "flex", gap: "8px", marginBottom: i < key_findings.length - 1 ? "8px" : 0 }}>
                            <span style={{ color: "#F6255A", flexShrink: 0 }}>•</span>
                            <p style={{ color: "#9ca3af", fontSize: "12px", margin: 0 }}>{finding}</p>
                        </div>
                    ))}
                </IntelCard>
            </div>

            {/* ── Social performance ────────────────────────────── */}
            <IntelCard title="Social Performance" icon="📱">
                {social.verdict && (
                    <p style={{ color: "#e5e7eb", fontSize: "13px", margin: "0 0 12px 0" }}>{social.verdict}</p>
                )}
                <div style={{ display: "flex", gap: "6px", flexWrap: "wrap", marginBottom: "16px" }}>
                    {social.best_platform && <Badge label={`Best: ${social.best_platform}`} color="#10b981" />}
                    {social.weakest_platform && <Badge label={`Weakest: ${social.weakest_platform}`} color="#ef4444" />}
                </div>

                {socialPlatforms.length > 0 && (
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))", gap: "12px", marginBottom: "16px" }}>
                        {socialPlatforms.map((p, i) => (
                            <div key={i} style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.07)", borderRadius: "10px", padding: "12px" }}>
                                <div className="d-flex align-items-center justify-content-between mb-2">
                                    <span style={{ color: "#fff", fontWeight: 600, fontSize: "13px", textTransform: "capitalize" }}>{p.platform}</span>
                                    {p.priority && <Badge label={p.priority} color={severityColor(p.priority)} />}
                                </div>
                                {p.read && <p style={{ color: "#9ca3af", fontSize: "12px", margin: "0 0 6px 0" }}>{p.read}</p>}
                                {p.action && <p style={{ color: "#e5e7eb", fontSize: "12px", margin: 0 }}>→ {p.action}</p>}
                            </div>
                        ))}
                    </div>
                )}

                {accountFixes.length > 0 && (
                    <div style={{ marginBottom: captionKeywords.length > 0 ? "12px" : 0 }}>
                        <p style={{ color: "#9ca3af", fontSize: "11px", textTransform: "uppercase", letterSpacing: "0.5px", margin: "0 0 8px 0" }}>Account Fixes</p>
                        {accountFixes.map((fix, i) => (
                            <div key={i} style={{ background: "rgba(239,68,68,0.06)", border: "1px solid rgba(239,68,68,0.15)", borderRadius: "8px", padding: "10px", marginBottom: i < accountFixes.length - 1 ? "8px" : 0 }}>
                                <p style={{ color: "#9ca3af", fontSize: "12px", margin: 0 }}>{fix}</p>
                            </div>
                        ))}
                    </div>
                )}

                {captionKeywords.length > 0 && (
                    <div>
                        <p style={{ color: "#9ca3af", fontSize: "11px", textTransform: "uppercase", letterSpacing: "0.5px", margin: "0 0 8px 0" }}>Caption & Ad Keywords</p>
                        <ExpandableBadgeList items={captionKeywords} color="#3b82f6" limit={8} />
                    </div>
                )}
{/* 
                {meta.captions_analyzed != null && (
                    <p style={{ color: "#6b7280", fontSize: "11px", margin: "12px 0 0 0" }}>
                        Based on {meta.captions_analyzed} caption{meta.captions_analyzed === 1 ? "" : "s"} analyzed
                        {meta.keyword_source ? ` · Keyword source: ${meta.keyword_source}` : ""}
                    </p>
                )} */}
            </IntelCard>

            {/* ── Competitors + Actions ─────────────────────────── */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px", marginTop: "12px" }}>
                <IntelCard title="Competitive Threats" icon="🏆" style={{ marginBottom: 0 }}>
                    {meta.competitors != null && (
                        <p style={{ color: "#6b7280", fontSize: "11px", margin: "-10px 0 12px 0" }}>
                            Based on {meta.competitors} competitor{meta.competitors === 1 ? "" : "s"} analyzed
                            {meta.competitors_with_social_data != null ? ` (${meta.competitors_with_social_data} with social data)` : ""}
                        </p>
                    )}
                    {competitors.length === 0 ? (
                        <p style={{ color: "#9ca3af", fontSize: "13px", margin: 0 }}>No competitor threats identified.</p>
                    ) : competitors.map((c, i) => (
                        <div key={i} style={{ background: "rgba(255,255,255,0.03)", borderRadius: "8px", padding: "10px", marginBottom: i < competitors.length - 1 ? "8px" : 0 }}>
                            <div className="d-flex align-items-center justify-content-between mb-1">
                                <span style={{ color: "#fff", fontWeight: 600, fontSize: "13px" }}>{c.name}</span>
                                {c.threat && <Badge label={`${c.threat} threat`} color={severityColor(c.threat)} />}
                            </div>
                            {c.why && <p style={{ color: "#9ca3af", fontSize: "12px", margin: "0 0 4px 0" }}>{c.why}</p>}
                            {c.exploit && <p style={{ color: "#10b981", fontSize: "12px", margin: 0 }}>💡 {c.exploit}</p>}
                        </div>
                    ))}
                </IntelCard>

                <IntelCard title="Action Feed" icon="⚡" style={{ marginBottom: 0 }}>
                    {actions.length === 0 ? (
                        <p style={{ color: "#9ca3af", fontSize: "13px", margin: 0 }}>No actions recommended right now.</p>
                    ) : actions.map((a, i) => (
                        <div key={i} style={{ background: "rgba(255,255,255,0.03)", borderRadius: "8px", padding: "10px", marginBottom: i < actions.length - 1 ? "8px" : 0 }}>
                            <div className="d-flex align-items-center justify-content-between mb-1">
                                <span style={{ color: "#e5e7eb", fontSize: "12px", fontWeight: 600, flex: 1 }}>{a.do}</span>
                                {a.priority && <Badge label={a.priority} color={severityColor(a.priority)} />}
                            </div>
                            <div style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
                                {a.area && <Badge label={a.area} color="#3b82f6" />}
                                {a.effort && <Badge label={`Effort: ${a.effort}`} color="#8b5cf6" />}
                                {a.kpi && <Badge label={`KPI: ${a.kpi}`} color="#6b7280" />}
                            </div>
                        </div>
                    ))}
                </IntelCard>
            </div>

            {/* ── Content plan ──────────────────────────────────── */}
            {content_plan.length > 0 && (
                <IntelCard title="Content Plan" icon="📅" style={{ marginTop: "12px", marginBottom: 0 }}>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: "12px" }}>
                        {content_plan.map((c, i) => (
                            <div key={i} style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.07)", borderRadius: "10px", padding: "12px" }}>
                                <div className="d-flex align-items-center gap-2 mb-1">
                                    {c.week && <Badge label={c.week} color="#F6255A" />}
                                    {c.platform && <Badge label={c.platform} color="#3b82f6" />}
                                </div>
                                {c.post && <p style={{ color: "#e5e7eb", fontSize: "12px", margin: "8px 0 4px 0" }}>{c.post}</p>}
                                {c.goal && <p style={{ color: "#9ca3af", fontSize: "11px", margin: 0 }}>Goal: {c.goal}</p>}
                            </div>
                        ))}
                    </div>
                </IntelCard>
            )}
        </>
    )
}

// ─── Ads Intelligence section ────────────────────────────────────────────────

const AdCard = ({ ad, appId, onOutboundClick }) => (
    <div style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.07)", borderRadius: "10px", padding: "12px" }}>
        {(ad.image_url || ad.thumbnail_url) && (
            <img
                src={ad.image_url || ad.thumbnail_url}
                alt=""
                style={{ width: "100%", height: "120px", objectFit: "cover", borderRadius: "8px", marginBottom: "8px" }}
            />
        )}
        <p style={{ color: "#fff", fontSize: "13px", margin: "0 0 8px 0" }}>
            {ad.ad_copy || ad.text || ad.body || ad.title || "Untitled ad"}
        </p>
        <div style={{ display: "flex", gap: "6px", flexWrap: "wrap", marginBottom: "8px" }}>
            {ad.platform && <Badge label={ad.platform} color="#3b82f6" />}
            {ad.ad_type && <Badge label={ad.ad_type} color="#8b5cf6" />}
            {ad.active != null && <Badge label={ad.active ? "Active" : "Inactive"} color={ad.active ? "#10b981" : "#6b7280"} />}
        </div>
        {(ad.impressions_est != null || ad.impressions != null) && (
            <p style={{ color: "#9ca3af", fontSize: "12px", margin: 0 }}>
                Est. Impressions: <strong style={{ color: "#fff" }}>{formatDownloads(ad.impressions_est ?? ad.impressions)}</strong>
            </p>
        )}
        {ad.link && (
            <a
                href={ad.link}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => onOutboundClick && onOutboundClick({ app_id: appId, platform: ad.platform, link_type: "ad" })}
                style={{ color: "#F6255A", fontSize: "12px", display: "inline-flex", alignItems: "center", gap: "4px", marginTop: "6px" }}
            >
                View Ad <ExternalLink size={12} />
            </a>
        )}
    </div>
)

const CompetitorCard = ({ competitor: c, ads, appId, onOutboundClick }) => (
    <div style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.07)", borderRadius: "10px", padding: "14px" }}>
        <div className="d-flex align-items-center gap-2 mb-2">
            {c.image_url && (
                <img src={c.image_url} alt={c.name} style={{ width: "40px", height: "40px", borderRadius: "8px", objectFit: "cover", flexShrink: 0 }} />
            )}
            <div style={{ flex: 1, minWidth: 0 }}>
                <p style={{ color: "#fff", fontWeight: 600, fontSize: "14px", margin: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {c.name}
                </p>
                <p style={{ color: "#9ca3af", fontSize: "11px", margin: 0 }}>
                    ⭐ {typeof c.rating === "number" ? c.rating.toFixed(2) : c.rating} · {formatDownloads(c.review_count || 0)} reviews
                </p>
            </div>
            {c.largest_by_volume && <Badge label="Top Volume" color="#f59e0b" />}
        </div>

        {c.genres?.length > 0 && (
            <div style={{ display: "flex", gap: "4px", flexWrap: "wrap", marginBottom: "8px" }}>
                {c.genres.map((g, i) => <Badge key={i} label={g} color="#3b82f6" />)}
            </div>
        )}

        {c.llm_feature && (
            <p style={{
                color: "#9ca3af", fontSize: "12px", margin: "0 0 8px 0",
                display: "-webkit-box", WebkitLineClamp: 3, WebkitBoxOrient: "vertical", overflow: "hidden",
            }}>
                {c.llm_feature}
            </p>
        )}

        {ads && (
            <div style={{ display: "flex", gap: "12px", marginBottom: "8px" }}>
                <span style={{ color: "#9ca3af", fontSize: "11px" }}>Ads: <strong style={{ color: "#fff" }}>{ads.ad_count ?? 0}</strong></span>
                <span style={{ color: "#9ca3af", fontSize: "11px" }}>Active: <strong style={{ color: "#fff" }}>{ads.active_ad_count ?? 0}</strong></span>
                <span style={{ color: "#9ca3af", fontSize: "11px" }}>Impr.: <strong style={{ color: "#fff" }}>{formatDownloads(ads.total_impressions_est || 0)}</strong></span>
            </div>
        )}

        {c.store_url && (
            <a
                href={c.store_url}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => onOutboundClick && onOutboundClick({ app_id: appId, platform: null, link_type: "competitor_listing" })}
                style={{ color: "#F6255A", fontSize: "12px", display: "inline-flex", alignItems: "center", gap: "4px" }}
            >
                View Listing <ExternalLink size={12} />
            </a>
        )}
    </div>
)

const AdsIntelligence = ({ data, appId, onOutboundClick }) => {
    const [showAllCompetitors, setShowAllCompetitors] = useState(false)

    if (!data) return null

    if (data.job_status !== "completed") {
        return (
            <div className="text-center py-5" style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.07)", borderRadius: "14px" }}>
                <Spinner style={{ color: "#F6255A" }} />
                <p className="mt-3 text-white" style={{ fontFamily: "Rubik" }}>{getFriendlyStatusMessage(data.job_status, "Loading ads data...")}</p>
            </div>
        )
    }

    const ownAds = data.ads || {}
    const competitors = data.competitors || []
    const competitorAds = data.competitor_ads || {}

    return (
        <>
            <IntelCard title={`${ownAds.app_name || "This App"}'s Ads`} icon="📢">
                <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "12px", marginBottom: "16px" }}>
                    {[
                        { label: "Total Ads", value: ownAds.ad_count ?? 0 },
                        { label: "Active Ads", value: ownAds.active_ad_count ?? 0 },
                        { label: "Est. Impressions", value: formatDownloads(ownAds.total_impressions_est || 0) },
                    ].map(({ label, value }) => (
                        <div key={label} style={{ background: "rgba(255,255,255,0.03)", borderRadius: "8px", padding: "14px", textAlign: "center" }}>
                            <p style={{ color: "#9ca3af", fontSize: "11px", margin: "0 0 6px 0", textTransform: "uppercase", letterSpacing: "0.5px" }}>{label}</p>
                            <p style={{ color: "#F6255A", fontWeight: 700, fontSize: "22px", margin: 0, fontFamily: "monospace" }}>{value}</p>
                        </div>
                    ))}
                </div>

                {(ownAds.platforms?.length > 0 || ownAds.ad_types?.length > 0) && (
                    <div style={{ display: "flex", gap: "6px", flexWrap: "wrap", marginBottom: "12px" }}>
                        {ownAds.platforms?.map((p, i) => <Badge key={`p-${i}`} label={p} color="#3b82f6" />)}
                        {ownAds.ad_types?.map((t, i) => <Badge key={`t-${i}`} label={t} color="#8b5cf6" />)}
                    </div>
                )}

                {!ownAds.ad_count ? (
                    <p style={{ color: "#9ca3af", fontSize: "13px", margin: 0 }}>No active ads found for this app.</p>
                ) : (
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: "12px" }}>
                        {(ownAds.ads || []).map((ad, i) => <AdCard key={ad.id || i} ad={ad} appId={appId} onOutboundClick={onOutboundClick} />)}
                    </div>
                )}
            </IntelCard>

            {competitors.length > 0 && (
                <IntelCard title="Competitors" icon="🏆" style={{ marginBottom: 0 }}>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "12px" }}>
                        {(showAllCompetitors ? competitors : competitors.slice(0, 3)).map(c => (
                            <CompetitorCard key={c.item_id} competitor={c} ads={competitorAds[c.item_id]} appId={appId} onOutboundClick={onOutboundClick} />
                        ))}
                    </div>
                    {competitors.length > 3 && (
                        <div className="text-center" style={{ marginTop: "16px" }}>
                            <button
                                onClick={() => setShowAllCompetitors(v => !v)}
                                style={{
                                    background: "rgba(246,37,90,0.1)",
                                    border: "1px solid #F6255A",
                                    color: "#F6255A",
                                    padding: "10px 24px",
                                    borderRadius: "10px",
                                    fontWeight: 600,
                                    fontFamily: "Rubik",
                                    fontSize: "13px",
                                    cursor: "pointer",
                                }}
                            >
                                {showAllCompetitors ? "Show Less" : `Show More (${competitors.length - 3})`}
                            </button>
                        </div>
                    )}
                </IntelCard>
            )}
        </>
    )
}

// ─── SEO Intelligence section ────────────────────────────────────────────────

const SeoIntelligence = ({ data }) => {
    if (!data) return null

    if (data.job_status !== "completed") {
        return (
            <div className="text-center py-5" style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.07)", borderRadius: "14px" }}>
                <Spinner style={{ color: "#F6255A" }} />
                <p className="mt-3 text-white" style={{ fontFamily: "Rubik" }}>{getFriendlyStatusMessage(data.job_status, "Loading SEO data...")}</p>
            </div>
        )
    }

    const {
        seo_score = 0,
        max_score = 10,
        competitors_analyzed,
        keywords_used = [],
        missing_keywords = [],
        recommended_keywords = [],
        keyword_frequency = {},
        content_gaps = [],
        optimization_suggestions = [],
    } = data

    const scorePct = max_score ? (seo_score / max_score) * 100 : 0
    const topKeywordLabels = Object.entries(keyword_frequency)
        .sort((a, b) => b[1] - a[1])
        .map(([kw, count]) => `${kw} · ${count}`)

    return (
        <>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 2fr", gap: "12px", marginBottom: "12px" }}>
                <div style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.07)", borderRadius: "14px", padding: "20px", textAlign: "center" }}>
                    <p style={{ color: "#9ca3af", fontSize: "12px", marginBottom: "8px", textTransform: "uppercase", letterSpacing: "0.5px" }}>SEO Score</p>
                    <div style={{ position: "relative", display: "inline-block" }}>
                        <svg width="80" height="80" viewBox="0 0 80 80">
                            <circle cx="40" cy="40" r="32" fill="none" stroke="rgba(255,255,255,0.05)" strokeWidth="8" />
                            <circle cx="40" cy="40" r="32" fill="none" stroke="#F6255A" strokeWidth="8"
                                strokeDasharray={`${(scorePct / 100) * 201} 201`}
                                strokeLinecap="round" transform="rotate(-90 40 40)" />
                        </svg>
                        <span style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", color: "#fff", fontWeight: 700, fontSize: "18px" }}>
                            {seo_score}/{max_score}
                        </span>
                    </div>
                    {competitors_analyzed != null && (
                        <p style={{ color: "#9ca3af", fontSize: "12px", margin: "10px 0 0 0" }}>
                            Based on {competitors_analyzed} competitors
                        </p>
                    )}
                </div>

                <IntelCard title="Top Keyword Frequency" icon="🔤" style={{ marginBottom: 0 }}>
                    <ExpandableBadgeList items={topKeywordLabels} color="#3b82f6" limit={6} emptyText="No keyword frequency data." />
                </IntelCard>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "12px", marginBottom: "12px" }}>
                <IntelCard title="Keywords Used" icon="✅" style={{ marginBottom: 0 }}>
                    <ExpandableBadgeList items={keywords_used} color="#10b981" limit={6} emptyText="No keywords used yet." />
                </IntelCard>
                <IntelCard title="Missing Keywords" icon="⚠️" style={{ marginBottom: 0 }}>
                    <ExpandableBadgeList items={missing_keywords} color="#ef4444" limit={6} emptyText="No missing keywords found." />
                </IntelCard>
                <IntelCard title="Recommended Keywords" icon="💡" style={{ marginBottom: 0 }}>
                    <ExpandableBadgeList items={recommended_keywords} color="#f59e0b" limit={6} emptyText="No recommendations yet." />
                </IntelCard>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
                <IntelCard title="Content Gaps" icon="🕳️" style={{ marginBottom: 0 }}>
                    {content_gaps.map((gap, i) => (
                        <div key={i} style={{ background: "rgba(239,68,68,0.06)", border: "1px solid rgba(239,68,68,0.15)", borderRadius: "8px", padding: "10px", marginBottom: i < content_gaps.length - 1 ? "8px" : 0 }}>
                            <p style={{ color: "#9ca3af", fontSize: "12px", margin: 0 }}>{gap}</p>
                        </div>
                    ))}
                </IntelCard>
                <IntelCard title="Optimization Suggestions" icon="🚀" style={{ marginBottom: 0 }}>
                    {optimization_suggestions.map((s, i) => (
                        <div key={i} style={{ background: "rgba(16,185,129,0.06)", border: "1px solid rgba(16,185,129,0.2)", borderRadius: "8px", padding: "10px", marginBottom: i < optimization_suggestions.length - 1 ? "8px" : 0 }}>
                            <p style={{ color: "#9ca3af", fontSize: "12px", margin: 0 }}>{s}</p>
                        </div>
                    ))}
                </IntelCard>
            </div>
        </>
    )
}

// ─── Social Platforms section ────────────────────────────────────────────────

const SocialPlatformCard = ({ platformKey, apps, totalApps, appId, onOutboundClick }) => {
    const meta = getPlatformMeta(platformKey)
    const [expanded, setExpanded] = useState(false)
    const VISIBLE_COUNT = 5
    const visibleApps = expanded ? apps : apps.slice(0, VISIBLE_COUNT)
    const pct = totalApps ? Math.round((apps.length / totalApps) * 100) : null

    return (
        <div style={{
            background: "rgba(255,255,255,0.03)",
            border: "1px solid rgba(255,255,255,0.07)",
            borderTop: `3px solid ${meta.color}`,
            borderRadius: "10px",
            padding: "16px",
        }}>
            <div className="d-flex align-items-center gap-2 mb-2">
                <div style={{
                    width: "34px", height: "34px", borderRadius: "8px",
                    background: `${meta.color}18`,
                    display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                }}>
                    <i className={meta.icon} style={{ color: meta.color, fontSize: "15px" }} />
                </div>
                <span style={{ color: "#fff", fontWeight: 600, fontSize: "14px", flex: 1 }}>{meta.displayName}</span>
                {pct != null && (
                    <span style={{ color: meta.color, fontWeight: 700, fontSize: "20px", fontFamily: "monospace" }}>{pct}%</span>
                )}
            </div>

            <div style={{ height: "5px", background: "rgba(255,255,255,0.05)", borderRadius: "3px", marginBottom: "6px" }}>
                <div style={{ height: "5px", width: `${pct ?? 0}%`, background: meta.color, borderRadius: "3px" }} />
            </div>
            <p style={{ color: "#6b7280", fontSize: "11px", margin: "0 0 14px 0" }}>
                {apps.length}{totalApps ? ` of ${totalApps} apps` : " apps"}
            </p>

            <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                {visibleApps.map(a => (
                    <a
                        key={a.item_id}
                        href={a.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => onOutboundClick && onOutboundClick({ app_id: appId, platform: platformKey, link_type: "platform_row" })}
                        style={{
                            display: "flex", justifyContent: "space-between", alignItems: "center",
                            padding: "7px 10px", borderRadius: "7px", textDecoration: "none",
                            background: a.is_target ? "rgba(246,37,90,0.1)" : "rgba(255,255,255,0.02)",
                            border: a.is_target ? "1px solid rgba(246,37,90,0.3)" : "1px solid transparent",
                        }}
                    >
                        <span style={{
                            color: a.is_target ? "#F6255A" : "#e5e7eb",
                            fontWeight: a.is_target ? 600 : 400,
                            fontSize: "12.5px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                        }}>
                            {a.name}{a.is_target ? " · This app" : ""}
                        </span>
                        <ExternalLink size={12} color="#6b7280" style={{ flexShrink: 0, marginLeft: "8px" }} />
                    </a>
                ))}
            </div>

            {apps.length > VISIBLE_COUNT && (
                <button
                    onClick={() => setExpanded(v => !v)}
                    style={{
                        marginTop: "10px", background: "transparent", border: "none",
                        color: meta.color, fontSize: "12px", fontWeight: 600, cursor: "pointer", padding: 0,
                    }}
                >
                    {expanded ? "Show less" : `+${apps.length - VISIBLE_COUNT} more`}
                </button>
            )}
        </div>
    )
}

const SocialPlatformsIntelligence = ({ data, appId, onOutboundClick }) => {
    if (!data) return null

    if (data.job_status !== "completed") {
        return (
            <div className="text-center py-5" style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.07)", borderRadius: "14px" }}>
                <Spinner style={{ color: "#F6255A" }} />
                <p className="mt-3 text-white" style={{ fontFamily: "Rubik" }}>{getFriendlyStatusMessage(data.job_status, "Loading social platforms data...")}</p>
            </div>
        )
    }

    const platformCounts = data.platform_counts || {}
    const platforms = data.platforms || {}
    const sortedPlatformKeys = Object.entries(platformCounts).sort((a, b) => b[1] - a[1]).map(([key]) => key)

    return (
        <>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "12px", marginBottom: "16px" }}>
                {[
                    { label: "Platforms Detected", value: sortedPlatformKeys.length },
                    { label: "Apps Analyzed", value: data.apps_analyzed ?? 0 },
                    { label: "Competitors", value: data.competitors_count ?? 0 },
                ].map(({ label, value }) => (
                    <div key={label} style={{ background: "rgba(255,255,255,0.03)", borderRadius: "8px", padding: "14px", textAlign: "center" }}>
                        <p style={{ color: "#9ca3af", fontSize: "11px", margin: "0 0 6px 0", textTransform: "uppercase", letterSpacing: "0.5px" }}>{label}</p>
                        <p style={{ color: "#F6255A", fontWeight: 700, fontSize: "22px", margin: 0, fontFamily: "monospace" }}>{value}</p>
                    </div>
                ))}
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: "12px" }}>
                {sortedPlatformKeys.map(key => (
                    <SocialPlatformCard key={key} platformKey={key} apps={platforms[key] || []} totalApps={data.apps_analyzed} appId={appId} onOutboundClick={onOutboundClick} />
                ))}
            </div>
        </>
    )
}

// ─── Platform metadata ────────────────────────────────────────────────────────

const PLATFORM_META = {
    youtube:   { icon: "fab fa-youtube",   color: "#FF0000", displayName: "YouTube"      },
    x:         { icon: "fab fa-twitter",   color: "#1DA1F2", displayName: "X (Twitter)"  },
    twitter:   { icon: "fab fa-twitter",   color: "#1DA1F2", displayName: "X (Twitter)"  },
    instagram: { icon: "fab fa-instagram", color: "#E4405F", displayName: "Instagram"    },
    facebook:  { icon: "fab fa-facebook",  color: "#1877F2", displayName: "Facebook"     },
    tiktok:    { icon: "fab fa-tiktok",    color: "#010101", displayName: "TikTok"       },
    discord:   { icon: "fab fa-discord",   color: "#5865F2", displayName: "Discord"      },
    reddit:    { icon: "fab fa-reddit",    color: "#FF4500", displayName: "Reddit"       },
    linkedin:  { icon: "fab fa-linkedin",  color: "#0077B5", displayName: "LinkedIn"     },
    snapchat:  { icon: "fab fa-snapchat",  color: "#FFFC00", displayName: "Snapchat"     },
}

const getPlatformMeta = (name = "") => {
    const key = name.toLowerCase()
    return PLATFORM_META[key] || { icon: "fas fa-globe", color: "#9ca3af", displayName: name }
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

// Maps a single app's POST /socials/social-accounts/ response into
// { platformDisplayName -> platformObj } for the followers/accounts table.
const transformSocialAccountsResponse = (apiData = {}) => {
    const platforms = {}

    ;(apiData.accounts || []).forEach(acct => {
        const meta = getPlatformMeta(acct.platform)
        const counts = acct.counts || {}
        const followers = counts.followers ?? counts.subscribers ?? null
        const metricLabel = counts.followers == null && counts.subscribers != null ? "subscribers" : null
        const avgInteractionsPerPost = counts.avg_interactions_per_post ?? null
        const engagementRate = avgInteractionsPerPost != null && followers
            ? (avgInteractionsPerPost / followers) * 100
            : null

        platforms[meta.displayName] = {
            displayName: meta.displayName,
            icon: meta.icon,
            color: meta.color,
            handle: acct.handle || acct.display_name,
            profileUrl: acct.url,
            followers,
            followersDisplay: null,
            metricLabel,
            verified: !!acct.verified,
            bio: acct.bio || null,
            following: counts.following ?? null,
            postsCount: counts.posts_count ?? counts.videos_count ?? null,
            avgInteractionsPerPost,
            engagementRate,
            lastScrapedAt: acct.last_scraped_at || null,
        }
    })

    // Linked-but-not-scraped platforms (e.g. Discord/Reddit) - no follower counts yet,
    // just a link, so only add them if we don't already have scraped data for that platform.
    ;(apiData.other_links || []).forEach(link => {
        const meta = getPlatformMeta(link.platform)
        if (platforms[meta.displayName]) return

        platforms[meta.displayName] = {
            displayName: meta.displayName,
            icon: meta.icon,
            color: meta.color,
            handle: null,
            profileUrl: link.url,
            followers: null,
            followersDisplay: null,
            metricLabel: null,
            verified: false,
            bio: null,
            following: null,
            postsCount: null,
            avgInteractionsPerPost: null,
            engagementRate: null,
            lastScrapedAt: null,
        }
    })

    return platforms
}

// ─── Component ────────────────────────────────────────────────────────────────

const SocialMediaComparison = ({ appId: propAppId, appName: propAppName, imageUrl: propImageUrl, onBack } = {}) => {
    const { trackEvent } = useAnalytics()
    const [searchParams] = useSearchParams()
    const [searchTerm, setSearchTerm] = useState("")
    const [loading, setLoading] = useState(false)
    const [showDetailsModal, setShowDetailsModal] = useState(false)
    const [showUpgradeModal, setShowUpgradeModal] = useState(false)
    const [isHovered, setIsHovered] = useState(false)

    // Single-app view
    const [selectedApp, setSelectedApp] = useState(null)
    const [socialData, setSocialData] = useState({}) // { platformDisplayName -> platformObj }
    const [socialAccountsSummary, setSocialAccountsSummary] = useState(null) // raw `summary` block from /socials/social-accounts/
    const [dataLoading, setDataLoading] = useState(false)

    // Which intelligence section (seo/marketing/ads/platforms) is expanded - only one at a time
    const [expandedSection, setExpandedSection] = useState("seo")

    // Social ads data (from POST /socials/ads/)
    const [socialAdsData, setSocialAdsData] = useState(null)
    const adsPollTimeoutRef = useRef(null)

    // Social SEO data (from POST /socials/seo/)
    const [socialSeoData, setSocialSeoData] = useState(null)
    const seoPollTimeoutRef = useRef(null)

    // Social platforms data (from POST /socials/social-platforms/)
    const [socialPlatformsData, setSocialPlatformsData] = useState(null)
    const platformsPollTimeoutRef = useRef(null)

    // Marketing intelligence data (from POST /socials/marketing-intelligence/)
    const [socialMarketingData, setSocialMarketingData] = useState(null)
    const marketingPollTimeoutRef = useRef(null)

    // "Apps you last compared here" (RECENT_APPS_API.md, seo_agent feature -
    // this screen's own `/socials/seo/` call below is what populates it).
    const [recentApps, setRecentApps] = useState([])
    const [loadingRecentApps, setLoadingRecentApps] = useState(true)

    const loadRecentApps = async () => {
        try {
            const data = await fetchRecentApps("seo_agent", { limit: 10 })
            setRecentApps(data?.recent_apps || [])
        } catch (err) {
            console.error("Error fetching recent apps:", err)
        } finally {
            setLoadingRecentApps(false)
        }
    }

    useEffect(() => { loadRecentApps() }, [])

    // ── Fetch social accounts/followers data for an app (POST /socials/social-accounts/) ─

    const fetchSocialData = async (appId) => {
        const response = await axios.post(
            `${okara_api}/socials/social-accounts/`,
            { app_id: appId },
            {
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${localStorage.getItem("authToken")}`,
                },
            }
        )
        return {
            platforms: transformSocialAccountsResponse(response.data || {}),
            summary: response.data?.summary || null,
        }
    }

    // ── Fetch social ads data for an app (POST /socials/ads/) ────────────────
    // Polls every 5s while the backend reports the ads job is still processing.

    const ADS_POLL_INTERVAL_MS = 5000
    // Backend reports progress via intermediate job_status stages (e.g. "resolving_platform").
    // Keep polling on anything except these terminal states, since new stage names can be added.
    const ADS_TERMINAL_STATUSES = ["completed", "success", "done", "failed", "error"]

    const fetchSocialAds = async (itemId) => {
        const response = await axios.post(
            `${okara_api}/socials/ads/`,
            { item_id: itemId },
            {
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${localStorage.getItem("authToken")}`,
                },
            }
        )
        return response.data
    }

    const stopAdsPolling = () => {
        if (adsPollTimeoutRef.current) {
            clearTimeout(adsPollTimeoutRef.current)
            adsPollTimeoutRef.current = null
        }
    }

    const pollSocialAds = async (itemId) => {
        try {
            const data = await fetchSocialAds(itemId)
            console.log("[socials/ads] response:", data)
            setSocialAdsData(data)

            const status = String(
                data?.job_status || data?.status || data?.ads_status || ""
            ).toLowerCase()

            if (status && !ADS_TERMINAL_STATUSES.includes(status)) {
                adsPollTimeoutRef.current = setTimeout(() => pollSocialAds(itemId), ADS_POLL_INTERVAL_MS)
            }
        } catch (err) {
            console.error("Error fetching social ads data:", err)
        }
    }

    useEffect(() => {
        return () => stopAdsPolling()
    }, [])

    // ── Fetch social SEO data for an app (POST /socials/seo/) ────────────────
    // Polls every 5s while the backend reports the seo job is still processing.

    const SEO_POLL_INTERVAL_MS = 5000
    const SEO_TERMINAL_STATUSES = ["completed", "success", "done", "failed", "error"]

    const fetchSocialSeo = async (itemId) => {
        const response = await axios.post(
            `${okara_api}/socials/seo/`,
            { item_id: itemId },
            {
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${localStorage.getItem("authToken")}`,
                },
            }
        )
        return response.data
    }

    const stopSeoPolling = () => {
        if (seoPollTimeoutRef.current) {
            clearTimeout(seoPollTimeoutRef.current)
            seoPollTimeoutRef.current = null
        }
    }

    const pollSocialSeo = async (itemId) => {
        try {
            const data = await fetchSocialSeo(itemId)
            console.log("[socials/seo] response:", data)
            setSocialSeoData(data)

            const status = String(
                data?.job_status || data?.status || data?.seo_status || ""
            ).toLowerCase()

            if (status && !SEO_TERMINAL_STATUSES.includes(status)) {
                seoPollTimeoutRef.current = setTimeout(() => pollSocialSeo(itemId), SEO_POLL_INTERVAL_MS)
            }
        } catch (err) {
            console.error("Error fetching social seo data:", err)
        }
    }

    useEffect(() => {
        return () => stopSeoPolling()
    }, [])

    // ── Fetch social platforms data for an app (POST /socials/social-platforms/) ─
    // Polls every 5s while the backend reports the job is still processing.

    const PLATFORMS_POLL_INTERVAL_MS = 5000
    const PLATFORMS_TERMINAL_STATUSES = ["completed", "success", "done", "failed", "error"]

    const fetchSocialPlatforms = async (itemId) => {
        const response = await axios.post(
            `${okara_api}/socials/social-platforms/`,
            { item_id: itemId },
            {
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${localStorage.getItem("authToken")}`,
                },
            }
        )
        return response.data
    }

    const stopPlatformsPolling = () => {
        if (platformsPollTimeoutRef.current) {
            clearTimeout(platformsPollTimeoutRef.current)
            platformsPollTimeoutRef.current = null
        }
    }

    const pollSocialPlatforms = async (itemId) => {
        try {
            const data = await fetchSocialPlatforms(itemId)
            console.log("[socials/social-platforms] response:", data)
            setSocialPlatformsData(data)

            const status = String(
                data?.job_status || data?.status || ""
            ).toLowerCase()

            if (status && !PLATFORMS_TERMINAL_STATUSES.includes(status)) {
                platformsPollTimeoutRef.current = setTimeout(() => pollSocialPlatforms(itemId), PLATFORMS_POLL_INTERVAL_MS)
            }
        } catch (err) {
            console.error("Error fetching social platforms data:", err)
        }
    }

    useEffect(() => {
        return () => stopPlatformsPolling()
    }, [])

    // ── Fetch marketing intelligence for an app (POST /socials/marketing-intelligence/) ─
    // Polls every 5s while the backend reports the job is still processing.

    const MARKETING_POLL_INTERVAL_MS = 5000
    const MARKETING_TERMINAL_STATUSES = ["completed", "success", "done", "failed", "error"]

    const fetchSocialMarketing = async (appId) => {
        const response = await axios.post(
            `${okara_api}/socials/marketing-intelligence/`,
            { app_id: appId },
            {
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${localStorage.getItem("authToken")}`,
                },
            }
        )
        return response.data
    }

    const stopMarketingPolling = () => {
        if (marketingPollTimeoutRef.current) {
            clearTimeout(marketingPollTimeoutRef.current)
            marketingPollTimeoutRef.current = null
        }
    }

    const pollSocialMarketing = async (appId) => {
        try {
            const data = await fetchSocialMarketing(appId)
            console.log("[socials/marketing-intelligence] response:", data)
            setSocialMarketingData(data)

            const status = String(
                data?.job_status || data?.status || ""
            ).toLowerCase()

            if (status && !MARKETING_TERMINAL_STATUSES.includes(status)) {
                marketingPollTimeoutRef.current = setTimeout(() => pollSocialMarketing(appId), MARKETING_POLL_INTERVAL_MS)
            }
        } catch (err) {
            console.error("Error fetching marketing intelligence data:", err)
        }
    }

    useEffect(() => {
        return () => stopMarketingPolling()
    }, [])

    // ── App selection (single view) ──────────────────────────────────────────

    const handleAppSelect = async (app) => {
        setSelectedApp(app)
        setSocialData({})
        setSocialAccountsSummary(null)
        setDataLoading(true)
        setShowDetailsModal(false)
        setExpandedSection("seo")

        stopAdsPolling()
        setSocialAdsData(null)

        stopSeoPolling()
        setSocialSeoData(null)

        stopPlatformsPolling()
        setSocialPlatformsData(null)

        stopMarketingPolling()
        setSocialMarketingData(null)

        const gameId = app.item_id || app._id

        pollSocialAds(gameId)
        pollSocialSeo(gameId)
        pollSocialPlatforms(gameId)
        pollSocialMarketing(gameId)

        try {
            const { platforms, summary } = await fetchSocialData(gameId)
            setSocialData(platforms || {})
            setSocialAccountsSummary(summary)
            trackEvent(ANALYTICS_EVENTS.FEATURE.SOCIAL_COMPARISON_APP_SELECTED, { app_id: gameId, app_name: app.name })
        } catch (err) {
            console.error("Error fetching social data:", err)
            toastr.error("Failed to fetch social media data.")
        } finally {
            setDataLoading(false)
        }

        // The recent-apps list moves as a side effect of the /socials/seo/ call
        // kicked off above (pollSocialSeo), not of reading this endpoint back -
        // refetch shortly after so this app jumps to the front without a reload.
        setTimeout(loadRecentApps, 1500)
    }

    const handleSelectRecentApp = (app) => {
        handleAppSelect({ item_id: app.app_id, name: app.name, image_url: app.image_url })
    }

    // Deep-link support: if the page is opened at /social-media-comparison?item_id=...
    // (e.g. clicked from a Dashboard app preview), load that app's data on mount.
    useEffect(() => {
        // When embedded elsewhere (e.g. the game dashboard) there's no
        // ?item_id= query param, so fall back to the props passed by the
        // embedding page.
        const itemId = searchParams.get("item_id") || propAppId
        if (itemId) {
            handleAppSelect({
                item_id: itemId,
                name: searchParams.get("name") || propAppName || "",
                image_url: searchParams.get("image_url") || propImageUrl || "",
            })
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const handleBackToSearch = () => {
        setSelectedApp(null)
        setSocialData({})
        setSocialAccountsSummary(null)
    }

    const handleSearch = () => {
        if (!searchTerm.trim()) return
        setLoading(true)
        setTimeout(() => { setLoading(false); setShowDetailsModal(true) }, 300)
    }

    const handleOutboundClick = ({ app_id, platform, link_type }) => {
        trackEvent(ANALYTICS_EVENTS.FEATURE.SOCIAL_COMPARISON_OUTBOUND_CLICKED, { app_id, platform, link_type })
    }

    const handleSectionToggle = (section) => {
        setExpandedSection(prev => {
            const next = prev === section ? null : section
            if (next) {
                trackEvent(ANALYTICS_EVENTS.FEATURE.SOCIAL_COMPARISON_SECTION_VIEWED, {
                    app_id: selectedApp?.item_id || selectedApp?._id,
                    section,
                })
            }
            return next
        })
    }

    const platforms = Object.values(socialData)

    // ── Styles ────────────────────────────────────────────────────────────────

    const btnPrimary = {
        padding: "8px 16px",
        borderRadius: "10px",
        background: isHovered ? "#e11d4c" : "#F6255A",
        color: "#fff",
        border: "none",
        fontWeight: "600",
        cursor: "pointer",
        whiteSpace: "nowrap",
        transition: "all 0.2s ease-in-out",
        height: "44px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: "8px",
        fontSize: "15px",
        fontFamily: "Rubik",
    }

    return (
        <React.Fragment>
            <SEO
                title={PAGE_SEO.socialMediaComparison.title}
                description={PAGE_SEO.socialMediaComparison.description}
                keywords={PAGE_SEO.socialMediaComparison.keywords}
                canonical={generateCanonicalUrl('/social-media-comparison')}
                robots={PAGE_SEO.socialMediaComparison.robots}
            />

            <div className="page-content">
                <div className="container-fluid" style={{ fontFamily: "Rubik, sans-serif" }}>

                    {/* ── Header ─────────────────────────────────────────── */}
                    <div className="mb-4 mt-2">
                        <div className="d-flex justify-content-between align-items-center">
                            <h1 className="mb-0 text-white" style={{ fontWeight: 700, fontSize: "28px" }}>
                                Social Media Comparison
                            </h1>
                            {onBack && <BackButton onClick={onBack} />}
                        </div>
                        <p style={{ fontSize: "15px", maxWidth: "900px", color: "#9ca3af", fontWeight: 400, marginTop: "8px", marginBottom: "20px" }}>
                            Analyze and compare the social media presence of any XR or VR app — track follower counts, handles, and platform reach across YouTube, Instagram, X, TikTok, Discord, and more.
                        </p>

                        {/* ── Summary cards ──────────────────────────────── */}
                        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "12px" }}>
                            {[
                                {
                                    icon: "fab fa-youtube",
                                    color: "#FF0000",
                                    title: "Multi-Platform Coverage",
                                    desc: "View social presence across YouTube, Instagram, X, TikTok, Discord, Reddit and more in one place.",
                                },
                                {
                                    icon: "fas fa-chart-bar",
                                    color: "#F6255A",
                                    title: "Follower Analytics",
                                    desc: "See exact follower counts, display-formatted numbers, and metric labels per platform per app.",
                                },
                                {
                                    icon: "fas fa-bullseye",
                                    color: "#f59e0b",
                                    title: "Marketing Intelligence",
                                    desc: "Get AI-powered campaign suggestions, ASO insights, content strategy, and action plans per app.",
                                },
                            ].map((item, i) => (
                                <div key={i} style={{
                                    background: "#21222d",
                                    border: "1px solid rgba(255,255,255,0.06)",
                                    borderRadius: "14px",
                                    padding: "16px",
                                    display: "flex",
                                    gap: "14px",
                                    alignItems: "flex-start",
                                }}>
                                    <div style={{
                                        width: "36px", height: "36px", borderRadius: "10px",
                                        background: `${item.color}18`,
                                        display: "flex", alignItems: "center", justifyContent: "center",
                                        flexShrink: 0,
                                    }}>
                                        <i className={item.icon} style={{ color: item.color, fontSize: "16px" }} />
                                    </div>
                                    <div>
                                        <p style={{ color: "#fff", fontWeight: 600, fontSize: "13px", margin: "0 0 4px 0" }}>{item.title}</p>
                                        <p style={{ color: "#9ca3af", fontSize: "12px", margin: 0, lineHeight: "1.5" }}>{item.desc}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* ── Search bar — hidden once an app's social data is being shown ── */}
                    {!selectedApp && (
                        <div style={{
                            padding: "30px",
                            background: "#21222d",
                            border: "1px solid rgba(255,255,255,0.05)",
                            borderRadius: "20px",
                            marginBottom: "24px",
                        }}>
                            <div className="mb-4">
                                <h2 className="text-white mb-2" style={{ fontWeight: 600, fontSize: '21.6px' }}>Find an App</h2>
                                <p style={{ fontSize: "14px", color: "#e8e8e8" }}>Search for any app to view its social media presence.</p>
                            </div>
                            <div className="d-flex align-items-center flex-column flex-sm-row gap-3">
                                <div className="d-flex align-items-center flex-grow-1" style={{
                                    maxWidth: "700px",
                                    background: "#21222d",
                                    border: "1px solid rgba(255,255,255,0.1)",
                                    borderRadius: "12px",
                                }}>
                                    <i className="fa-solid fa-magnifying-glass ms-3" style={{ color: "#adb5bd" }} />
                                    <input
                                        type="text"
                                        placeholder="Enter App or Game name"
                                        className="flex-grow-1"
                                        style={{ background: "transparent", border: "none", color: "#fff", padding: "12px", fontFamily: "Rubik", minWidth: 0 }}
                                        value={searchTerm}
                                        onChange={e => setSearchTerm(e.target.value)}
                                        onKeyDown={e => e.key === "Enter" && handleSearch()}
                                    />
                                </div>
                                <button
                                    onClick={handleSearch}
                                    onMouseEnter={() => setIsHovered(true)}
                                    onMouseLeave={() => setIsHovered(false)}
                                    style={{ ...btnPrimary, width: "120px" }}
                                >
                                    {loading ? <Spinner size="sm" /> : <Search size={16} />}
                                    Search
                                </button>
                            </div>
                        </div>
                    )}

                    {!selectedApp && (
                        <RecentApps apps={recentApps} loading={loadingRecentApps} onSelect={handleSelectRecentApp} />
                    )}

                    {/* ── Main content ───────────────────────────────────── */}
                    {!selectedApp ? (
                        (loadingRecentApps || recentApps.length === 0) && (
                        <div className="text-center py-5" style={{
                            background: "#21222d",
                            borderRadius: "24px",
                            border: "1px dashed rgba(255,255,255,0.1)",
                        }}>
                            <div style={{ maxWidth: "500px", margin: "0 auto" }}>
                                <div style={{
                                    width: "80px", height: "80px",
                                    background: "rgba(246,37,90,0.05)",
                                    borderRadius: "50%",
                                    display: "flex", alignItems: "center", justifyContent: "center",
                                    margin: "0 auto 24px",
                                    border: "1px solid rgba(246,37,90,0.2)",
                                }}>
                                    <Gamepad2 size={32} color="#F6255A" />
                                </div>
                                <h2 className="text-white mb-3" style={{ fontSize: '21.6px' }}>Get Started</h2>
                                <p style={{ lineHeight: "1.6", color: "#e8e8e8" }} className="mb-4">
                                    Search for an app above to view its social media presence.
                                </p>
                                <button
                                    onClick={() => setShowDetailsModal(true)}
                                    style={{
                                        background: "rgba(246,37,90,0.1)",
                                        border: "1px solid #F6255A",
                                        color: "#F6255A",
                                        padding: "12px 40px",
                                        borderRadius: "12px",
                                        fontWeight: "600",
                                        fontFamily: "Rubik",
                                        cursor: "pointer",
                                    }}
                                >
                                    Start Exploring
                                </button>
                            </div>
                        </div>
                        )
                    ) : (
                        <Card className="border-0" style={{ background: "#21222d", borderRadius: "16px" }}>
                            <CardBody className="p-4">

                                {/* ── App header ─────────────────────────── */}
                                <div className="d-flex align-items-center justify-content-between mb-4">
                                    <div className="d-flex align-items-center gap-3">
                                        <button
                                            onClick={handleBackToSearch}
                                            className="btn btn-sm"
                                            style={{ background: "rgba(255,255,255,0.05)", border: "none", color: "#fff", borderRadius: "8px", padding: "8px 12px" }}
                                        >
                                            <ArrowLeft size={18} />
                                        </button>
                                        <img
                                            src={selectedApp.image_url}
                                            alt={selectedApp.name}
                                            style={{ width: "56px", height: "56px", borderRadius: "12px", objectFit: "cover" }}
                                        />
                                        <div>
                                            <h2 className="text-white mb-1" style={{ fontWeight: 600, fontSize: "20px" }}>
                                                {selectedApp.name}
                                            </h2>
                                            <span style={{ fontSize: "13px", color: "#9ca3af" }}>Social Media Presence</span>
                                        </div>
                                    </div>
                                </div>

                                {/* ── Accounts summary strip ─────────────── */}
                                {!dataLoading && socialAccountsSummary && (
                                    <div style={{
                                        display: "flex", flexWrap: "wrap", gap: "16px", alignItems: "center",
                                        background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.07)",
                                        borderRadius: "12px", padding: "14px 20px", marginBottom: "16px",
                                    }}>
                                        <div>
                                            <p style={{ color: "#9ca3af", fontSize: "11px", margin: "0 0 4px 0", textTransform: "uppercase", letterSpacing: "0.5px" }}>Total Followers</p>
                                            <p style={{ color: "#F6255A", fontWeight: 700, fontSize: "20px", margin: 0, fontFamily: "monospace" }}>
                                                {formatDownloads(socialAccountsSummary.total_followers || 0)}
                                            </p>
                                        </div>
                                        <div style={{ width: "1px", height: "32px", background: "rgba(255,255,255,0.08)" }} />
                                        <span style={{ color: "#9ca3af", fontSize: "13px" }}>
                                            {socialAccountsSummary.accounts_count ?? 0} platform{(socialAccountsSummary.accounts_count ?? 0) === 1 ? "" : "s"} updated
                                        </span>
                                    </div>
                                )}

                                {/* ── Platform table ─────────────────────── */}
                                {dataLoading ? (

                                    <div className="text-center py-5">
                                        <Spinner style={{ color: "#F6255A" }} />
                                        <p className="mt-3 text-white" style={{ fontFamily: "Rubik" }}>Loading social media data...</p>
                                    </div>
                                ) : platforms.length === 0 ? (
                                    <div className="text-center py-5">
                                        <i className="fas fa-globe" style={{ fontSize: "48px", color: "#374151", marginBottom: "16px" }} />
                                        <p style={{ color: "#9ca3af" }}>No social media data found for this app.</p>
                                    </div>
                                ) : (
                                    <div className="table-responsive">
                                        <table className="table table-nowrap align-middle mb-0" style={{ background: "transparent", minWidth: "600px" }}>
                                            <thead>
                                                <tr style={{ borderBottom: "1px solid rgba(255,255,255,0.08)", background: "rgba(255,255,255,0.02)" }}>
                                                    {["Platform", "Handle", "Followers", "Engagement", "Link"].map(col => (
                                                        <th key={col} style={{
                                                            fontSize: "13px", fontWeight: 600,
                                                            textTransform: "uppercase", letterSpacing: "0.5px",
                                                            padding: "16px 20px", borderBottom: "none", color: "#adb5bd",
                                                        }}>
                                                            {col}
                                                        </th>
                                                    ))}
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {platforms.map((p, idx) => {
                                                    const hasData = Boolean(p.handle) || p.followers != null || p.avgInteractionsPerPost != null
                                                    return !hasData ? (
                                                    <tr
                                                        key={idx}
                                                        style={{
                                                            borderBottom: idx === platforms.length - 1 ? "none" : "1px solid rgba(255,255,255,0.04)",
                                                        }}
                                                    >
                                                        <td style={{ padding: "16px 20px", borderBottom: "none" }}>
                                                            <div className="d-flex align-items-center gap-3">
                                                                <div style={{
                                                                    width: "36px", height: "36px", borderRadius: "8px",
                                                                    background: `${p.color}18`,
                                                                    display: "flex", alignItems: "center", justifyContent: "center",
                                                                    flexShrink: 0, opacity: 0.5,
                                                                }}>
                                                                    <i className={p.icon} style={{ color: p.color, fontSize: "18px" }} />
                                                                </div>
                                                                <span className="text-white" style={{ fontSize: "15px", fontWeight: 500, opacity: 0.6 }}>
                                                                    {p.displayName}
                                                                </span>
                                                            </div>
                                                        </td>
                                                        <td colSpan={3} style={{ padding: "16px 20px", borderBottom: "none" }}>
                                                            <div className="d-flex align-items-center gap-2" style={{
                                                                background: "rgba(255,255,255,0.03)",
                                                                border: "1px dashed rgba(255,255,255,0.1)",
                                                                borderRadius: "8px",
                                                                padding: "8px 14px",
                                                                width: "fit-content",
                                                            }}>
                                                                <i className="fas fa-link-slash" style={{ color: "#6b7280", fontSize: "12px" }} />
                                                                <span style={{ color: "#9ca3af", fontSize: "13px" }}>
                                                                    No {p.displayName} profile found for this app
                                                                </span>
                                                            </div>
                                                        </td>
                                                        {/* Link — still shown if we have a profile URL, even with no stats */}
                                                        <td style={{ padding: "16px 20px", borderBottom: "none" }}>
                                                            {p.profileUrl ? (
                                                                <a
                                                                    href={p.profileUrl}
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                    onClick={() => handleOutboundClick({ app_id: selectedApp?.item_id || selectedApp?._id, platform: p.displayName, link_type: "profile" })}
                                                                    style={{ color: "#9ca3af", transition: "color 0.2s" }}
                                                                    onMouseEnter={e => e.currentTarget.style.color = "#F6255A"}
                                                                    onMouseLeave={e => e.currentTarget.style.color = "#9ca3af"}
                                                                >
                                                                    <ExternalLink size={16} />
                                                                </a>
                                                            ) : (
                                                                <span style={{ color: "#6b7280" }}>—</span>
                                                            )}
                                                        </td>
                                                    </tr>
                                                    ) : (
                                                    <tr
                                                        key={idx}
                                                        title={p.lastScrapedAt ? `Last scraped: ${new Date(p.lastScrapedAt).toLocaleString()}` : undefined}
                                                        style={{
                                                            borderBottom: idx === platforms.length - 1 ? "none" : "1px solid rgba(255,255,255,0.04)",
                                                            transition: "background 0.2s ease",
                                                        }}
                                                        onMouseEnter={e => e.currentTarget.style.background = "rgba(255,255,255,0.03)"}
                                                        onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                                                    >
                                                        {/* Platform */}
                                                        <td style={{ padding: "16px 20px", borderBottom: "none" }}>
                                                            <div className="d-flex align-items-center gap-3">
                                                                <div style={{
                                                                    width: "36px", height: "36px", borderRadius: "8px",
                                                                    background: `${p.color}18`,
                                                                    display: "flex", alignItems: "center", justifyContent: "center",
                                                                    flexShrink: 0,
                                                                }}>
                                                                    <i className={p.icon} style={{ color: p.color, fontSize: "18px" }} />
                                                                </div>
                                                                <span className="text-white" style={{ fontSize: "15px", fontWeight: 500 }}>
                                                                    {p.displayName}
                                                                </span>
                                                            </div>
                                                        </td>
                                                        {/* Handle */}
                                                        <td style={{ padding: "16px 20px", borderBottom: "none" }}>
                                                            <div className="d-flex align-items-center gap-1">
                                                                <span style={{ color: "#9ca3af", fontSize: "14px" }}>
                                                                    {p.handle || "—"}
                                                                </span>
                                                                {p.verified && (
                                                                    <i className="fas fa-check-circle" title="Verified" style={{ color: "#3b82f6", fontSize: "12px" }} />
                                                                )}
                                                            </div>
                                                            {p.bio && (
                                                                <p style={{
                                                                    color: "#6b7280", fontSize: "11px", margin: "4px 0 0 0", maxWidth: "260px",
                                                                    overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                                                                }}>
                                                                    {p.bio}
                                                                </p>
                                                            )}
                                                        </td>
                                                        {/* Followers */}
                                                        <td style={{ padding: "16px 20px", borderBottom: "none" }}>
                                                            {p.followers != null ? (
                                                                <span style={{
                                                                    background: "rgba(246,37,90,0.1)", color: "#F6255A",
                                                                    fontSize: "12px", fontWeight: 600,
                                                                    padding: "6px 12px", borderRadius: "8px", display: "inline-block",
                                                                }}>
                                                                    {p.followersDisplay || formatDownloads(p.followers)}
                                                                    {p.metricLabel ? <span style={{ color: "#9ca3af", fontWeight: 400, marginLeft: "4px" }}>({p.metricLabel})</span> : null}
                                                                </span>
                                                            ) : (
                                                                <span style={{ color: "#6b7280", fontSize: "14px" }}>—</span>
                                                            )}
                                                        </td>
                                                        {/* Engagement */}
                                                        <td style={{ padding: "16px 20px", borderBottom: "none" }}>
                                                            {p.avgInteractionsPerPost != null ? (
                                                                <span style={{ color: "#e5e7eb", fontSize: "13px" }}>
                                                                    {formatDownloads(p.avgInteractionsPerPost)} avg.
                                                                    {p.engagementRate != null && (
                                                                        <span style={{ color: "#10b981", fontWeight: 600, marginLeft: "4px" }}>
                                                                            ({p.engagementRate.toFixed(2)}%)
                                                                        </span>
                                                                    )}
                                                                </span>
                                                            ) : (
                                                                <span style={{ color: "#6b7280", fontSize: "14px" }}>—</span>
                                                            )}
                                                        </td>
                                                        {/* Link */}
                                                        <td style={{ padding: "16px 20px", borderBottom: "none" }}>
                                                            {p.profileUrl ? (
                                                                <a
                                                                    href={p.profileUrl}
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                    onClick={() => handleOutboundClick({ app_id: selectedApp?.item_id || selectedApp?._id, platform: p.displayName, link_type: "profile" })}
                                                                    style={{ color: "#9ca3af", transition: "color 0.2s" }}
                                                                    onMouseEnter={e => e.currentTarget.style.color = "#F6255A"}
                                                                    onMouseLeave={e => e.currentTarget.style.color = "#9ca3af"}
                                                                >
                                                                    <ExternalLink size={16} />
                                                                </a>
                                                            ) : (
                                                                <span style={{ color: "#6b7280" }}>—</span>
                                                            )}
                                                        </td>
                                                    </tr>
                                                    )
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                )}

                                {/* ── SEO Intelligence ───────────────────── */}
                                {socialSeoData && (
                                    <CollapsibleSection
                                        title="SEO Intelligence"
                                        isOpen={expandedSection === "seo"}
                                        onToggle={() => handleSectionToggle("seo")}
                                    >
                                        <SeoIntelligence data={socialSeoData} />
                                    </CollapsibleSection>
                                )}

                                {/* ── Marketing Intelligence ──────────────── */}
                                {socialMarketingData && (
                                    <CollapsibleSection
                                        title="Marketing Intelligence"
                                        isOpen={expandedSection === "marketing"}
                                        onToggle={() => handleSectionToggle("marketing")}
                                    >
                                        <MarketingIntelligence data={socialMarketingData} />
                                    </CollapsibleSection>
                                )}

                                {/* ── Ads Intelligence ───────────────────── */}
                                {socialAdsData && (
                                    <CollapsibleSection
                                        title="Ads Intelligence"
                                        isOpen={expandedSection === "ads"}
                                        onToggle={() => handleSectionToggle("ads")}
                                    >
                                        <AdsIntelligence data={socialAdsData} appId={selectedApp?.item_id || selectedApp?._id} onOutboundClick={handleOutboundClick} />
                                    </CollapsibleSection>
                                )}

                                {/* ── Social Platforms ───────────────────── */}
                                {socialPlatformsData && (
                                    <CollapsibleSection
                                        title="Social Platforms"
                                        isOpen={expandedSection === "platforms"}
                                        onToggle={() => handleSectionToggle("platforms")}
                                    >
                                        <SocialPlatformsIntelligence data={socialPlatformsData} appId={selectedApp?.item_id || selectedApp?._id} onOutboundClick={handleOutboundClick} />
                                    </CollapsibleSection>
                                )}
                            </CardBody>
                        </Card>
                    )}

                    <AppsTrackingPlanModal
                        show={showUpgradeModal}
                        handleClose={() => setShowUpgradeModal(false)}
                        title="Upgrade your plan to unlock this feature"
                    />

                    <KeywordSuggestionsDetailsModal
                        show={showDetailsModal}
                        handleClose={() => setShowDetailsModal(false)}
                        title="Search Apps"
                        searchTerm={searchTerm}
                        onSearchChange={e => setSearchTerm(e.target.value)}
                        onSearchClick={() => {}}
                        onSelectApp={handleAppSelect}
                        selectButtonLabel="View Social Data"
                    />
                </div>
            </div>
        </React.Fragment>
    )
}

export default SocialMediaComparison
